<template>
  <template v-if="showCenSchedule">
    <div
      v-if="fetchContractEventsAction.firstLoad.value"
      ref="cenContainer"
      class="w-full overflow-y-auto relative"
    >
      <div class="flex flex-col w-full border border-gray-200 rounded-lg shadow-sm">
        <div
          class="px-2 py-4 bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10"
          :class="{ 'z-30': increaseZIndex }"
        >
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                  <div class="space-y-2">
                    <div class="relative">
                      <AppInputIcon
                        v-model="searchForm.form.search"
                        type="text"
                        name="cen_schedule_filter"
                        class="form-input w-full"
                        :placeholder="$t('HeadContract.CenSchedule.placeholder.search')"
                      >
                        <template #icon>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
</svg>
                        </template>
                      </AppInputIcon>
                    </div>
                  </div>

                  <div class="space-y-2">
                    <AppSelect
                      v-model="searchForm.form.status"
                      name="status_filter"
                      class="w-full capitalize form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      :options="statusOptions"
                      :empty-text="$t('HeadContract.CenSchedule.placeholder.allStatuses')"
                    />
                  </div>

                  <div class="space-y-2">
                    <AppTypeAhead
                      v-model="nextActionUserName"
                      name="next_action_assignee_id"
                      :placeholder="$t('HeadContract.CenSchedule.placeholder.searchUsers')"
                      :suggestion-callback="getSuggestions"
                      :initial-suggestions="getSuggestions"
                      @value-selected="selectUser"
                    >
                      <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>
                      </template>
                    </AppTypeAhead>
                  </div>

                  <div class="space-y-2">
                    <AppSelect
                      v-model="sortColumn"
                      name="sort_filter"
                      class="w-full form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      :options="sortOptions"
                      :empty-text="$t('HeadContract.CenSchedule.placeholder.selectSortOption')"
                    />
                  </div>

                  <div class="space-y-2">
                    <button
                      type="button"
                      class="btn btn--secondary-blue w-full text-sm"
                      @click="resetSearch"
                    >
                      {{ $t('HeadContract.CenSchedule.button.clearFilters') }}
                    </button>
                  </div>
                </div>
              </div>

              <Transition
                mode="out-in"
                enter-active-class="animate__animated animate__fadeIn animate__faster"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
              >
                <p
                  v-if="totalContractEventsCount > 0"
                  key="total-cen"
                  class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                >
                  <span class="font-medium text-gray-900">{{ totalContractEventsCount }}&nbsp;</span>

                  <span class="text-gray-500">{{ $t('HeadContract.CenSchedule.cen', totalContractEventsCount) }}</span>
                </p>
              </Transition>
            </div>

            <div
              v-if="hasActiveFilters"
              class="flex items-center space-x-2 pt-2"
            >
              <span class="text-sm text-gray-500">{{ $t('HeadContract.CenSchedule.activeFilters') }}:</span>

              <div class="flex flex-wrap gap-2">
                <span
                  v-if="searchForm.form.search"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {{ $t('HeadContract.CenSchedule.search') }}: "{{ searchForm.form.search }}"
                  <button
                    type="button"
                    class="ml-1.5 text-blue-600 hover:text-blue-800"
                    @click="searchForm.form.search = ''; resetSearchIfNotDirty()"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                  </button>
                </span>

                <span
                  v-if="!falsey(searchForm.form.status)"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize"
                  :class="ContractEventStatusBadgeClasses[enumKeyFromValue(ContractEventStatus, searchForm.form.status)] ?? 'bg-gray-100 text-gray-800 border-gray-200'"
                >
                  {{ $t(`HeadContract.CenSchedule.statusTitles.${enumKeyFromValue(ContractEventStatus, searchForm.form.status)}`) }}
                  <button
                    type="button"
                    class="ml-1.5"
                    @click="searchForm.form.status = undefined; resetSearchIfNotDirty()"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                  </button>
                </span>

                <span
                  v-if="searchForm.form.next_action_assignee_id"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-300 text-gray-700"
                >
                  {{ selectedNextActionUserName }}
                  <button
                    type="button"
                    class="ml-1.5 text-gray-600 hover:text-gray-800"
                    @click="clearUserFilter"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>

        <table class="border-separate border-spacing-0 w-full z-10 text-sm">
          <thead>
            <tr class="child:px-2 child:py-3 z-10 sticky top-0 child:border-y child:border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
              <th class="text-left w-60">
                {{ $t('HeadContract.CenSchedule.event') }}
              </th>

              <th class="text-left ">
                {{ $t('HeadContract.CenSchedule.cause') }}
              </th>

              <th class="text-left">
                {{ $t('HeadContract.CenSchedule.effect') }}
              </th>

              <th class="text-left whitespace-nowrap">
                {{ $t('HeadContract.CenSchedule.dateOfOccurrence') }}
              </th>

              <th class="text-left whitespace-nowrap">
                Status
              </th>

              <th class="text-left whitespace-nowrap">
                {{ $t('HeadContract.CenSchedule.timeBar') }}
              </th>

              <th class="text-left w-72">
                {{ $t('HeadContract.CenSchedule.comments') }}
              </th>

              <th
                v-if="project?.unarchived"
                class="text-left w-96"
              >
                {{ $t('HeadContract.VariationsSchedule.assignee') }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="fetchContractEventsAction.is(States.LOADING)">
              <td
                :colspan="tableColspan"
                class="px-6 py-16 text-center"
              >
                <div class="flex flex-col items-center">
                  <AppSpinner class="h-8 w-8 mb-4" />

                  <h3 class="text-sm font-medium text-gray-900 mb-1">
                    {{ $t('HeadContract.CenSchedule.loading') }}
                  </h3>
                </div>
              </td>
            </tr>

            <template v-else-if="fetchContractEventsAction.is(States.COMPLETE) && filteredContractEvents.length > 0">
              <CenScheduleEventRow
                v-for="event in filteredContractEvents"
                :key="event.id"
                :contract-event="event"
                @open-details="emit('open-details', $event)"
                @open-comment-modal="emit('open-comment-modal', $event)"
                @save-next-action="onSaveNextAction"
                @save-next-action-snapshot="onSaveNextActionSnapshot"
              />

              <tr v-if="hasMorePages || loadingMore">
                <td
                  :colspan="tableColspan"
                  class="px-6 py-4 text-center"
                >
                  <AppSpinner
                    v-if="loadingMore"
                    class="h-6 w-6 mx-auto"
                  />

                  <div
                    v-else-if="hasMorePages"
                    ref="loadMoreSentinelRef"
                    class="h-4"
                  />
                </td>
              </tr>
            </template>

            <tr v-else-if="fetchContractEventsAction.is(States.COMPLETE)">
              <td
                :colspan="tableColspan"
                class="px-6 py-16 text-center"
              >
                <div class="flex flex-col items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                  <h3 class="text-sm font-medium text-gray-900 mb-1">
                    {{ $t('HeadContract.CenSchedule.noEventsFound') }}
                  </h3>

                  <p class="text-sm text-gray-500">
                    {{ $t('HeadContract.CenSchedule.adjustFilters') }}
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <AppSpinner
      v-else
      class="mx-auto mt-5"
    />
  </template>

  <div
    v-else-if="fetchContractEventsAction.is(States.LOADING)"
    class="flex w-full py-16"
  >
    <AppSpinner class="mx-auto" />
  </div>

  <div
    v-else
    class="flex flex-col items-center py-12"
  >
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

    <h3 class="text-sm font-medium text-gray-900 mb-1">
      {{ $t('HeadContract.CenSchedule.noEventsFound') }}
    </h3>

    <p class="text-sm text-gray-500">
      {{ $t('HeadContract.CenSchedule.createFirstCen') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import CenScheduleEventRow from '@/components/HeadContract/CenSchedule/CenScheduleEventRow.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { enumKeyFromValue, falsey } from '@/helpers';
import { t } from '@/i18n';
import ContractEvent, { ContractEventStatus, ContractEventStatusBadgeClasses } from '@/models/ContractEvent';
import { useContractEventsStore } from '@/store/contractEvents';
import { useProjectsStore } from '@/store/projects';
import { useDebounceFn, useIntersectionObserver, useScroll } from '@vueuse/core';
import { useCollect } from 'pinia-orm/dist/helpers';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

const emit = defineEmits<{
  (e: 'open-details', contractEvent: ContractEvent): void;
  (e: 'open-comment-modal', contractEvent: ContractEvent): void;
}>();

const projectsStore = useProjectsStore();
const contractEventsStore = useContractEventsStore();
const totalContractEventsCount = ref(0);
const loadedContractEventIds = ref<number[]>([]);
const currentPage = ref(0);
const lastPage = ref(1);
const loadingMore = ref(false);
const loadMoreSentinelRef = useTemplateRef('loadMoreSentinelRef');

async function resetAndLoadContractEvents(
  projectId: Id,
  params: ApiRequests['api.v1.contract-events.index'] = {},
): Promise<void> {
  loadedContractEventIds.value = [];
  currentPage.value = 0;

  const paginator = await contractEventsStore.fetchContractEvents(projectId, { ...params, page: 1 });

  loadedContractEventIds.value = paginator.data.map((row: ContractEvent) => row.id);
  currentPage.value = paginator.meta.current_page;
  lastPage.value = paginator.meta.last_page;
  totalContractEventsCount.value = paginator.meta.total;
}

const fetchContractEventsAction = useStoreApiAction(resetAndLoadContractEvents);
const hasMorePages = computed(() => currentPage.value < lastPage.value);

const loadMoreContractEvents = async () => {
  const projectId = project.value?.id;

  if(!projectId || loadingMore.value || !hasMorePages.value || fetchContractEventsAction.is(States.LOADING)) {
    return;
  }

  loadingMore.value = true;

  try {
    const paginator = await contractEventsStore.fetchContractEvents(projectId, {
      ...searchForm.form,
      page: currentPage.value + 1,
    });

    loadedContractEventIds.value = [
      ...loadedContractEventIds.value,
      ...paginator.data.map((row: ContractEvent) => row.id),
    ];

    currentPage.value = paginator.meta.current_page;
    lastPage.value = paginator.meta.last_page;
    totalContractEventsCount.value = paginator.meta.total;
  } catch (error) {
    console.error(error);
  } finally {
    loadingMore.value = false;
  }
};

const updateNextActionContractEventAction = useStoreApiAction(contractEventsStore.updateNextActionContractEvent);
const saveNextActionSnapshotAction = useStoreApiAction(contractEventsStore.saveNextActionSnapshot);
const project = computed(() => projectsStore.project);

const searchForm = useFormData({
  search: '',
  status: undefined as ObjectValues<typeof ContractEventStatus> | undefined,
  next_action_assignee_id: undefined as number | undefined,
});

const nextActionUserName = ref('');
const selectedNextActionUserName = ref('');

watch(nextActionUserName, () => {
  searchForm.resetData(['next_action_assignee_id']);
});

const filterActive = ref(false);

const showCenSchedule = computed(() => {
  if(totalContractEventsCount.value > 0) {
    return true;
  }

  return fetchContractEventsAction.firstLoad.value && searchForm.isDirty.value;
});

const tableColspan = computed(() => {
  return project.value?.unarchived ? 8 : 6;
});

const searchContractEvents = () => {
  const projectId = project.value?.id;

  if(!projectId) {
    return;
  }

  fetchContractEventsAction.request(projectId, searchForm.form).catch((error) => {
    console.error(error);
  });
};

watch(
  () => project.value?.id,
  (newValue) => {
    if(newValue) {
      filterActive.value = false;
      searchForm.resetData();
      nextActionUserName.value = '';
      selectedNextActionUserName.value = '';
      searchContractEvents();
    }
  },
  { immediate: true },
);

const contractEvents = computed(() => {
  const projectId = project.value?.id;

  if(!projectId) {
    return [];
  }

  return contractEventsStore.models
    .where('project_id', Number(projectId))
    .with('user')
    .with('next_action_assignee')
    .with('variation_order')
    .with('delay')
    .with('comments', (query) => {
      query.with('creator');
    })
    .get();
});

const sortColumn = ref<'status' | 'date_of_occurrence' | 'created_at' | 'next_action'>('status');

const sortOptions = computed(() => {
  return [
    { value: 'status', label: t('HeadContract.CenSchedule.inputs.sortBy.status') },
    { value: 'date_of_occurrence', label: t('HeadContract.CenSchedule.inputs.sortBy.dateOfOccurrence') },
    { value: 'created_at', label: t('HeadContract.CenSchedule.inputs.sortBy.latest') },
    { value: 'next_action', label: t('HeadContract.CenSchedule.inputs.sortBy.nextActionDueDate') },
  ];
});

const sortedContractEvents = computed(() => {
  const rows = [...contractEvents.value];

  switch (sortColumn.value) {
    case 'status':
      return useCollect(rows).sortBy([[sortColumn.value, 'asc'], ['created_at', 'desc']]);

    case 'date_of_occurrence':
      return useCollect(rows).sortBy([[sortColumn.value, 'desc']]);

    case 'created_at':
      return useCollect(rows).sortBy([[sortColumn.value, 'desc']]);

    case 'next_action':
      return [...rows].sort((a, b) => {
        if(!falsey(a.nextActionDueIn) && !falsey(b.nextActionDueIn)) {
          if(a.nextActionDueIn! < 0) {
            return 1;
          }

          if(b.nextActionDueIn! < 0) {
            return -1;
          }

          return a.nextActionDueIn! - b.nextActionDueIn!;
        } else if(falsey(a.nextActionDueIn) && !falsey(b.nextActionDueIn)) {
          return 1;
        }

        return -1;
      });

    default:
      return rows;
  }
});

const filteredContractEvents = computed(() => {
  const loadedIds = new Set(loadedContractEventIds.value);

  return sortedContractEvents.value.filter((event) => loadedIds.has(event.id));
});

const resetSearch = () => {
  filterActive.value = false;
  searchForm.resetData();
  nextActionUserName.value = '';
  selectedNextActionUserName.value = '';
  searchContractEvents();
};

const resetSearchIfNotDirty = () => {
  if(
    searchForm.form.search === '' && searchForm.form.status === undefined &&
    searchForm.form.next_action_assignee_id === undefined
  ) {
    searchForm.resetData();
  }
};

const hasActiveFilters = computed(() => {
  return searchForm.isDirty.value;
});

const statusOptions = computed(() => {
  return Object.keys(ContractEventStatus)
    .filter((key) => Number.isNaN(Number(key)))
    .map((key) => ({
      value: ContractEventStatus[key as keyof typeof ContractEventStatus],
      label: t(`HeadContract.CenSchedule.statusTitles.${key}`),
    }));
});

const suggestedUsers = ref<{ id: number; name: string }[]>([]);

const getSuggestions = async () => {
  suggestedUsers.value = await projectsStore
    .searchUsers(project.value?.id, { name: nextActionUserName.value });

  return suggestedUsers.value.map((value) => value.name);
};

const selectUser = (index: number) => {
  nextTick(() => {
    if(suggestedUsers.value[index]) {
      searchForm.form.next_action_assignee_id = suggestedUsers.value[index].id;
      selectedNextActionUserName.value = suggestedUsers.value[index].name;
    }
  });
};

const clearUserFilter = () => {
  nextActionUserName.value = '';
  selectedNextActionUserName.value = '';
  searchForm.form.next_action_assignee_id = undefined;
  resetSearchIfNotDirty();
};

const debounceSearch = useDebounceFn(searchContractEvents, 300);

watch(searchForm.form, (newValue) => {
  if(
    !newValue.search && newValue.status === undefined && newValue.next_action_assignee_id === undefined &&
    filterActive.value
  ) {
    filterActive.value = false;
    searchContractEvents();
  } else {
    filterActive.value = true;
  }
});

watch(searchForm.form, () => {
  if(filterActive.value) {
    debounceSearch();
  }
});

const onSaveNextAction = (payload: {
  contractEvent: ContractEvent;
  next_action?: string;
  next_action_due_date?: string;
  next_action_assignee_id?: number;
}) => {
  updateNextActionContractEventAction
    .request(payload.contractEvent.id, {
      next_action: payload.next_action,
      next_action_due_date: payload.next_action_due_date,
      next_action_assignee_id: payload.next_action_assignee_id,
    })
    .catch(console.error);
};

const onSaveNextActionSnapshot = async (payload: {
  contractEvent: ContractEvent;
  next_action?: string;
  next_action_due_date?: string;
  next_action_assignee_id?: number;
}) => {
  try {
    await updateNextActionContractEventAction.request(payload.contractEvent.id, {
      next_action: payload.next_action,
      next_action_due_date: payload.next_action_due_date,
      next_action_assignee_id: payload.next_action_assignee_id,
    });

    await saveNextActionSnapshotAction.request(payload.contractEvent.id);
  } catch (error) {
    console.error(error);
  }
};

const cenContainer = useTemplateRef('cenContainer');
const { y } = useScroll(cenContainer);
const increaseZIndex = ref(true);

watch(y, (newValue) => {
  if(newValue > 0) {
    increaseZIndex.value = false;
  } else {
    increaseZIndex.value = true;
  }
});

useIntersectionObserver(
  loadMoreSentinelRef,
  ([{ isIntersecting }]) => {
    if(isIntersecting) {
      loadMoreContractEvents();
    }
  },
  {
    root: cenContainer,
    threshold: 0.1,
    rootMargin: '100px',
  },
);

defineExpose({
  reload: searchContractEvents,
});
</script>
